import { DatePicker } from '@mui/lab';
import { Grid } from '@mui/material';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { SectionVM } from 'civil-status/view-models/step-form';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { FormControl } from '@mui/material';
import { RadioGroup } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { Radio } from '@mui/material';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';

interface ActConsentProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  nothingRequired?: boolean;
}

const ActConsentComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  nothingRequired,
}: ActConsentProps): React.ReactElement => {
  const isCSad = useMemo(() => {
    return configuration === ConfigurationType.CSAD;
  }, [configuration]);

  const isEnteDich = useMemo(() => {
    return (
      configuration === ConfigurationType.ENTEDICH || configuration === ConfigurationType.ENTEDICHC
    );
  }, [configuration]);

  const isEnteDichS = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHS;
  }, [configuration]);

  const isEnteDichT = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHT;
  }, [configuration]);

  const isEnteDiche = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHE;
  }, [configuration]);

  // const isCSra = useMemo(() => {
  //   return configuration === ConfigurationType.CSra;
  // }, [configuration]);

  const isDpsm = useMemo(() => {
    return configuration === ConfigurationType.DPSM;
  }, [configuration]);

  const isTrDatiAtau = useMemo(() => {
    return configuration === ConfigurationType.TRDATIATAU;
  }, [configuration]);

  const isTrDatiAtin = useMemo(() => {
    return configuration === ConfigurationType.TRDATIATIN;
  }, [configuration]);

  const isSenproCrep = useMemo(() => {
    return configuration === ConfigurationType.SENPROCREP;
  }, [configuration]);

  const isProvCom = useMemo(() => {
    return configuration === ConfigurationType.PROVCOM;
  }, [configuration]);

  const isEnteDichif = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHIF;
  }, [configuration]);

  const isImpgen = useMemo(() => {
    return configuration === ConfigurationType.IMPGEN;
  }, [configuration]);

  const isImped01 = useMemo(() => {
    return configuration === ConfigurationType.IMPED01;
  }, [configuration]);

  const isImped02 = useMemo(() => {
    return configuration === ConfigurationType.IMPED02;
  }, [configuration]);

  const isEnteDichin = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHIN;
  }, [configuration]);

  const isEnteDicha = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHA;
  }, [configuration]);

  const isEnteDichnc = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHNC;
  }, [configuration]);

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values || {}).foreign,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChange('foreign', {
        ...(valuesNew || {}),
      });
    },
    [values, onChange]
  );

  useEffect(() => {
    if (isSenproCrep && !(values?.nameInstitution || values?.rolo)) {
      // settare i valori fissi
      onChanges({
        nameInstitution: 'Tribunale',
        rolo: 'Procuratore della Repubblica',
      });
    }
  }, [isSenproCrep, values?.nameInstitution, values?.rolo]);

  const isNotary = useMemo(() => {
    return isEnteDicha || isEnteDichnc ? values?.isNotary : false;
  }, [values?.isNotary, isEnteDicha, isEnteDichnc]);

  return (
    <Grid container spacing={2} item>
      {isEnteDicha || isEnteDichnc ? (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Ente dichiarante o notaio"
              id="isNotary"
              defaultValue={false}
              value={values?.isNotary || false}
              onChange={() =>
                onChanges({
                  isForeign: false,
                  isNotary: !values?.isNotary,
                  documentDateFormation: null,
                  documentExtremes: !values?.isNotary ? 'Rep... Racc...' : '',
                  nameInstitution: '',
                })
              }
            >
              <FormControlLabel value="false" control={<Radio />} label="Ente Dichiarante" />
              <FormControlLabel value="true" control={<Radio />} label="Notaio" />
            </RadioGroup>
          </FormControl>
        </Grid>
      ) : undefined}
      {isProvCom || isTrDatiAtin || ((isEnteDicha || isEnteDichnc) && !isNotary) ? undefined : (
        <Grid item xs={12} md={4}>
          <DatePicker
            label={`Data formazione documento ${nothingRequired ? '' : '*'}`}
            aria-label="Data formazione documento"
            value={values?.documentDateFormation || null}
            onChange={(date) => {
              onChange('documentDateFormation', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="documentDateFormation"
                fullWidth
                helperText={errors?.documentDateFormation}
                error={touched?.documentDateFormation && Boolean(errors?.documentDateFormation)}
              />
            )}
          />
        </Grid>
      )}
      {isProvCom ? undefined : (
        <Fragment>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label={`Estremi documento ${nothingRequired || isEnteDichT ? '' : '*'}`}
              aria-label="Estremi documento"
              id="documentExtremes"
              name="documentExtremes"
              fullWidth
              disabled={disabled}
              helperText={errors?.documentExtremes}
              error={touched?.documentExtremes && Boolean(errors?.documentExtremes)}
              type="text"
              variant="outlined"
              multiline
              value={values?.documentExtremes || ''}
              onChange={(event) => {
                onChange('documentExtremes', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label={`${isNotary ? 'Cognome e nome notaio' : 'Nome ente'} ${
                nothingRequired ? '' : '*'
              }`}
              aria-label={isNotary ? 'Cognome e nome notaio' : 'Nome ente'}
              id="nameInstitution"
              name="nameInstitution"
              fullWidth
              disabled={disabled || isSenproCrep}
              helperText={errors?.nameInstitution}
              error={touched?.nameInstitution && Boolean(errors?.nameInstitution)}
              type="text"
              variant="outlined"
              value={values?.nameInstitution || ''}
              onChange={(event) => {
                onChange('nameInstitution', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      )}
      {isEnteDichS ? (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data pubblicazione sentenza *"
              aria-label="Data pubblicazione sentenza"
              value={values?.judgmentPublicationDate || null}
              onChange={(date) => {
                onChange('judgmentPublicationDate', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="judgmentPublicationDate"
                  fullWidth
                  helperText={errors?.judgmentPublicationDate}
                  error={
                    touched?.judgmentPublicationDate && Boolean(errors?.judgmentPublicationDate)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data decorrenza sentenza"
              aria-label="Data decorrenza sentenza"
              value={values?.effectiveDateSentence || null}
              onChange={(date) => {
                onChange('effectiveDateSentence', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="effectiveDateSentence"
                  fullWidth
                  helperText={errors?.effectiveDateSentence}
                  error={touched?.effectiveDateSentence && Boolean(errors?.effectiveDateSentence)}
                />
              )}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isSenproCrep ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            label={`Ruolo ente dichiarante ${nothingRequired ? '' : '*'}`}
            aria-label="Ruolo ente dichiarante"
            id="rolo"
            name="rolo"
            fullWidth
            helperText={errors?.rolo}
            error={touched?.rolo && Boolean(errors?.rolo)}
            type="text"
            variant="outlined"
            multiline
            value={values?.rolo || ''}
            onChange={(event) => {
              onChange('rolo', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isCSad ||
      // isCSra TODO chiedere a SOGEI come è in questo caso
      isDpsm ||
      isTrDatiAtau ||
      isTrDatiAtin ||
      isEnteDich ||
      isEnteDichS ||
      isEnteDichT ||
      (isEnteDichnc && !isNotary) ? (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Stato"
              id="isForeign"
              value={values?.isForeign || 'false'}
              onChange={() => {
                onChanges({
                  ...(values || {}),
                  isForeign: !values?.isForeign,
                  italian: {
                    location: '',
                    province: '',
                    municipality: null,
                  },
                  foreign: {
                    state: null,
                    province: '',
                    municipality: null,
                  },
                });
              }}
            >
              <FormControlLabel
                value="false"
                control={<Radio />}
                label="Italia"
                disabled={disabled}
              />
              <FormControlLabel
                value="true"
                control={<Radio />}
                label="Estero"
                disabled={disabled}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      ) : undefined}
      {!isEnteDiche && // only foreign
      (!values?.isForeign ||
        !(
          isCSad || // isCSra TODO chiedere a SOGEI come è in questo caso
          isDpsm ||
          isTrDatiAtau ||
          isTrDatiAtin ||
          isEnteDich ||
          isEnteDichS ||
          isEnteDichT ||
          isEnteDichnc
        )) ? (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onChangeItalian}
          onChanges={onChangesItalian}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={isNotary ? 'Distretto notarile' : 'Comune ente'}
          labelProvince={isNotary ? 'Provincia' : 'Provincia ente'}
          required={!(isEnteDichif || isImpgen || nothingRequired)}
          fromDate={values?.documentDateFormation || null}
        />
      ) : (
        <Fragment>
          <Foreign
            disabledComponent={disabled}
            values={values?.foreign}
            onChange={onFieldForeignChange}
            onChanges={onFieldsForeignChange}
            labelState={isNotary ? 'Stato' : 'Stato ente'}
            errors={errors?.foreign as FormikErrors<ForeignForm>}
            touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
            required={!nothingRequired}
            isWithLocationForeign={
              isCSad ||
              isDpsm ||
              isEnteDich ||
              isEnteDichS ||
              isEnteDichT ||
              isTrDatiAtau ||
              isEnteDiche
            }
            isWithConsulate={isEnteDichnc}
          />
        </Fragment>
      )}
      {isTrDatiAtin || isEnteDichin || ((isEnteDicha || isEnteDichnc) && !isNotary) ? (
        <Grid item xs={12}>
          <PmiTextField
            label={`Indirizzo ente ${nothingRequired || isEnteDicha || isEnteDichnc ? '' : '*'}`}
            aria-label="Indirizzo"
            id="address"
            name="address"
            fullWidth
            disabled={disabled}
            helperText={errors?.address}
            error={touched?.address && Boolean(errors?.address)}
            type="text"
            variant="outlined"
            value={values?.address || ''}
            onChange={(event) => {
              onChange('address', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isEnteDiche || isEnteDichT || isEnteDichS ? (
        <Grid item xs={12}>
          <PmiTextField
            label={`Descrizione documento allegato ${
              nothingRequired || isEnteDichT || isEnteDichS ? '' : '*'
            }`}
            aria-label="Descrizione documento allegato"
            id="documentDescription"
            name="documentDescription"
            fullWidth
            disabled={disabled}
            helperText={errors?.documentDescription}
            error={touched?.documentDescription && Boolean(errors?.documentDescription)}
            type="text"
            variant="outlined"
            value={values?.documentDescription || ''}
            onChange={(event) => {
              onChange('documentDescription', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isImpgen || isImped01 || isImped02 ? (
        <Grid item xs={12}>
          <PmiTextField
            label={`Motivazione ${nothingRequired ? '' : '*'}`}
            aria-label="Motivazione"
            id="reason"
            name="reason"
            fullWidth
            helperText={errors?.reason}
            error={touched?.reason && Boolean(errors?.reason)}
            type="text"
            variant="outlined"
            multiline
            value={values?.reason || ''}
            onChange={(event) => {
              onChange('reason', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

ActConsentComponent.displayName = 'Form Act Consent component';

const ActConsent = memo(ActConsentComponent);

export default ActConsent;
